import type { PrintGatewayStatus } from "../gatewayStatus";
import { t } from "../i18n";
import { useState } from "react";
import {
  Bell,
  ChevronLeft,
  ChevronRight,
  Cloud,
  Download,
  LayoutGrid,
  LogOut,
  Moon,
  Printer,
  RefreshCw,
  Server,
  ShieldCheck,
  Smartphone,
  Sun,
  Type,
  Users
} from "lucide-react";
import type { ThemeColor, ThemePreference } from "../storage";
import type { ConnectionState } from "../useEasyOrders";
import type { Setup, User } from "../types";
import { EmployeesPage } from "../components/settings/EmployeesPage";
import { ServerPrintersPage } from "../components/settings/ServerPrintersPage";
import { AccountPage } from "../components/settings/AccountPage";
import { TextPage } from "../components/settings/TextPage";
import { UiPage } from "../components/settings/UiPage";
import { PrintPage } from "../components/settings/PrintPage";
import { NotificationsPage } from "../components/settings/NotificationsPage";
import { ThemePage } from "../components/settings/ThemePage";
import { APP_VERSION } from "../utils";
import { useBackHandler } from "../backNavigation";

declare const __BUILD_ID__: string;

type Page =
  | "menu"
  | "account"
  | "theme"
  | "text"
  | "ui"
  | "notifications"
  | "update"
  | "employees"
  | "server"
  | "print";

type Props = {
  username: string;
  businessName: string;
  connection: ConnectionState;
  lastSyncAt: number;
  deploymentMode: string;
  theme: ThemePreference;
  themeColor: ThemeColor;
  updateAvailable: boolean;
  checkingUpdate: boolean;
  installAvailable: boolean;
  isAdmin: boolean;
  currentUser: User;
  setup: Setup;
  busy: boolean;
  onSaveSetup: (next: Setup) => Promise<void>;
  onLoadGatewayStatus: () => Promise<PrintGatewayStatus | null>;
  onChangePassword: (password: string) => Promise<void>;
  onEnableMfa: () => Promise<void>;
  onDisableMfa: () => Promise<void>;
  onTestPrint: (printerId: number) => void;
  onProbePrinter: (printerId: number) => void;
  onThemeChange: (theme: ThemePreference) => void;
  onThemeColorChange: (color: ThemeColor) => void;
  onNotifications: () => Promise<NotificationPermission>;
  onSync: () => void;
  onInstall: () => void;
  onUpdate: () => void;
  onCheckUpdate: () => void;
  onLogout: () => void;
};

function MenuRow({
  title,
  subtitle,
  badge,
  icon,
  onClick
}: {
  title: string;
  subtitle: string;
  badge?: boolean;
  icon?: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button type="button" className="settings-row" onClick={onClick}>
      {badge ? (
        <span className="settings-icon-wrap">
          {icon ?? <RefreshCw />}
          <b>!</b>
        </span>
      ) : (
        icon ?? <span className="settings-row-icon-spacer" aria-hidden="true" />
      )}
      <span>
        <strong>{title}</strong>
        <small>{subtitle}</small>
      </span>
      <ChevronRight className="settings-row-chevron" aria-hidden="true" />
    </button>
  );
}

export function SettingsScreen({
  username,
  businessName,
  connection,
  lastSyncAt,
  deploymentMode,
  theme,
  themeColor,
  updateAvailable,
  checkingUpdate,
  installAvailable,
  isAdmin,
  currentUser,
  setup,
  busy,
  onSaveSetup,
  onLoadGatewayStatus,
  onChangePassword,
  onEnableMfa,
  onDisableMfa,
  onTestPrint,
  onProbePrinter,
  onThemeChange,
  onThemeColorChange,
  onNotifications,
  onSync,
  onInstall,
  onUpdate,
  onCheckUpdate,
  onLogout
}: Props) {
  const [page, setPage] = useState<Page>("menu");
  useBackHandler(page !== "menu", () => setPage("menu"), "page");

  const back = (
    <button type="button" className="secondary-command wide settings-back-command" onClick={() => setPage("menu")}> 
      <ChevronLeft />
      <span>{t("Πίσω στις ρυθμίσεις")}</span>
    </button>
  );

  if (page === "account") {
    return (
      <AccountPage
        back={back}
        currentUser={currentUser}
        onChangePassword={onChangePassword}
        onEnableMfa={onEnableMfa}
        onDisableMfa={onDisableMfa}
        onLogout={onLogout}
      />
    );
  }

  if (page === "text") {
    return <TextPage back={back} />;
  }

  if (page === "ui") {
    return <UiPage back={back} />;
  }

  if (page === "notifications") {
    return <NotificationsPage back={back} onRequestPermission={onNotifications} />;
  }

  if (page === "print") {
    return <PrintPage back={back} setup={setup} busy={busy} onSaveSetup={onSaveSetup} onTestPrint={onTestPrint} onProbePrinter={onProbePrinter} />;
  }

  if (page === "employees") {
    return <EmployeesPage back={back} setup={setup} currentUser={currentUser} onSaveSetup={onSaveSetup} />;
  }

  if (page === "server") {
    return <ServerPrintersPage back={back} setup={setup} onSaveSetup={onSaveSetup} onLoadGatewayStatus={onLoadGatewayStatus} />;
  }

  if (page === "theme") {
    return (
      <ThemePage
        back={back}
        theme={theme}
        themeColor={themeColor}
        onThemeChange={onThemeChange}
        onThemeColorChange={onThemeColorChange}
      />
    );
  }

  if (page === "update") {
    return (
      <section className="screen settings-screen" aria-labelledby="settings-heading">
        {back}
        <div className="screen-heading-row">
          <div>
            <h1 id="settings-heading">{t("Ενημέρωση εφαρμογής")}</h1>
            <p>{updateAvailable ? t("Νέα έκδοση διαθέσιμη") : t("Η εφαρμογή είναι ενημερωμένη")}</p>
          </div>
        </div>
        <div className="settings-groups">
          <section className="settings-group">
            <div className="status-row">
              <Smartphone />
              <span>
                <strong>{t("Έκδοση")}</strong>
                <small>PWA {APP_VERSION} • build {__BUILD_ID__}</small>
              </span>
            </div>
            {installAvailable ? (
              <button type="button" className="settings-row" onClick={onInstall}>
                <Download />
                <span>
                  <strong>{t("Εγκατάσταση εφαρμογής")}</strong>
                  <small>{t("Προσθήκη στην αρχική οθόνη")}</small>
                </span>
              </button>
            ) : null}
            {updateAvailable ? (
              <button type="button" className="settings-row update-row" onClick={onUpdate}>
                <span className="settings-icon-wrap">
                  <RefreshCw />
                  <b>!</b>
                </span>
                <span>
                  <strong>{t("Εγκατάσταση νέας έκδοσης")}</strong>
                  <small>{t("Η εφαρμογή θα επανεκκινήσει")}</small>
                </span>
              </button>
            ) : (
              <button type="button" className="settings-row" onClick={onCheckUpdate} disabled={checkingUpdate}>
                <RefreshCw />
                <span>
                  <strong>{checkingUpdate ? t("Έλεγχος...") : t("Έλεγχος για ενημέρωση")}</strong>
                  <small>{t("Έλεγχος τώρα")}</small>
                </span>
              </button>
            )}
          </section>
        </div>
      </section>
    );
  }

  return (
    <section className="screen settings-screen" aria-labelledby="settings-heading">
      <div className="screen-heading-row">
        <div>
          <h1 id="settings-heading">{t("Ρυθμίσεις")}</h1>
          <p>{isAdmin ? t("Διαχειριστής") : t("Λογαριασμός")}</p>
        </div>
      </div>

      <div className="settings-groups">
        <section className="settings-group">
          <MenuRow
            title={t("Λογαριασμός")}
            subtitle={t("Κωδικός πρόσβασης και αποσύνδεση")}
            icon={<Smartphone />}
            onClick={() => setPage("account")}
          />
          <MenuRow
            title={t("Θέμα")}
            subtitle={t("Χρώματα και φωτεινό/σκούρο")}
            icon={theme === "dark" ? <Moon /> : <Sun />}
            onClick={() => setPage("theme")}
          />
          <MenuRow
            title={t("Κείμενο")}
            subtitle={t("Γραμματοσειρά, μέγεθος και πάχος")}
            icon={<Type />}
            onClick={() => setPage("text")}
          />
          <MenuRow
            title={t("Εμφάνιση")}
            subtitle={t("Κύλιση προϊόντων και διάταξη")}
            icon={<LayoutGrid />}
            onClick={() => setPage("ui")}
          />
          <MenuRow
            title={t("Ενημέρωση εφαρμογής")}
            subtitle={updateAvailable ? t("Νέα έκδοση διαθέσιμη") : t("Έλεγχος και εγκατάσταση νέας έκδοσης")}
            icon={<Download />}
            badge={updateAvailable}
            onClick={() => {
              setPage("update");
              onCheckUpdate();
            }}
          />
          <MenuRow
            title={t("Ειδοποιήσεις")}
            subtitle={t("Ήχος και ειδοποιήσεις για αλλαγές παραγγελιών")}
            icon={<Bell />}
            onClick={() => setPage("notifications")}
          />
        </section>

        {isAdmin ? (
          <section className="settings-group">
            <h2>{t("Διαχείριση")}</h2>
            <MenuRow
              title={t("Υπάλληλοι")}
              subtitle={t("Λογαριασμοί, δικαιώματα και διαγραφή")}
              icon={<Users />}
              onClick={() => setPage("employees")}
            />
            <MenuRow
              title={t("Server / Εκτυπωτές")}
              subtitle={t("Server και ξεχωριστή ρύθμιση κάθε εκτυπωτή")}
              icon={<Server />}
              onClick={() => setPage("server")}
            />
            <MenuRow
              title={t("Εκτύπωση")}
              subtitle={t("Χαρτί, γράμματα, κενά και τρόπος εκτύπωσης")}
              icon={<Printer />}
              onClick={() => setPage("print")}
            />
          </section>
        ) : null}

        <section className="settings-group">
          <h2>{t("Σύνδεση")}</h2>
          <div className="status-row">
            <Cloud />
            <span>
              <strong>{connection === "online" ? t("Συνδεδεμένο") : t("Εκτός σύνδεσης")}</strong>
              <small>
                {lastSyncAt
                  ? t("Τελευταίος συγχρονισμός {0}", new Date(lastSyncAt).toLocaleTimeString("el-GR"))
                  : t("Δεν έχει συγχρονιστεί")}
              </small>
            </span>
          </div>
          <button type="button" className="settings-row" onClick={onSync}>
            <RefreshCw />
            <span>
              <strong>{t("Συγχρονισμός τώρα")}</strong>
              <small>{deploymentMode || "EasyOrders Cloud"}</small>
            </span>
          </button>
        </section>

        <section className="settings-group">
          <h2>{t("Διαγνωστικά και απόρρητο")}</h2>
          <div className="status-row diagnostics-privacy-row">
            <ShieldCheck />
            <span>
              <strong>{t("Αυτόματη τεχνική διάγνωση")}</strong>
              <small>{t("Για αξιοπιστία, ασφάλεια και επίλυση προβλημάτων, ο server συνδέει τεχνικά συμβάντα και αποτελέσματα ενεργειών με τον λογαριασμό/ρόλο και ένα ψευδωνυμοποιημένο αναγνωριστικό συσκευής. Δεν καταγράφονται κωδικοί πρόσβασης, tokens, ονόματα, σχόλια, σημειώσεις, αναζητήσεις, εικόνες ή το περιεχόμενο παραγγελιών. Δεν γίνεται καταγραφή οθόνης, αξιολόγηση εργαζομένων ή αποστολή σε υπηρεσία analytics τρίτου. Η ουρά της συσκευής κρατά έως 400 συμβάντα για επτά ημέρες. Από προεπιλογή, τα διαγνωστικά server κρατούνται 30 ημέρες και το επιχειρησιακό ιστορικό ελέγχου 365· ο φορέας της εγκατάστασης μπορεί να τα αλλάξει και η επιχείρηση/υπεύθυνος επεξεργασίας παρέχει την πλήρη ενημέρωση απορρήτου προς το προσωπικό.")}</small>
            </span>
          </div>
        </section>

        <section className="settings-group">
          <div className="status-row">
            <Smartphone />
            <span>
              <strong>{username}</strong>
              <small>{businessName} • build {__BUILD_ID__}</small>
            </span>
          </div>
          <button type="button" className="settings-row danger-row" onClick={onLogout}>
            <LogOut />
            <span>
              <strong>{t("Αποσύνδεση")}</strong>
            </span>
          </button>
        </section>
      </div>
    </section>
  );
}
